import React, { useEffect, useMemo, useState } from "react";
import {
  Company,
  Person,
  Address,
  CustomFieldValues,
  HydratedOpportunity,
  OpportunityStatistics as OpportunityStatisticsType,
  OPPORTUNITY_STATUS_DISPLAY_NAMES,
  GetOpportunityStatisticsResponseSchema,
  ListOpportunitiesResponseSchema,
  UpdateCompanyData,
  UpdateOpportunityParams,
  Permission,
} from "@forgepoint/shared";
import { Loader, ChevronDown, RotateCcw, Check } from "lucide-react";
import isEqual from "fast-deep-equal";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import { toast } from "sonner";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DataTable, DataTableColumn } from "@/components/ui/data-table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useCompaniesStore } from "./companiesStore";
import { useCompanyShippingAddressesStore } from "@/modules/companyShippingAddresses/companyShippingAddressesStore";
import { usePeopleStore } from "@/modules/people/peopleStore";
import { useOpportunitiesStore } from "@/modules/opportunities/opportunitiesStore";
import { usePriceListsStore } from "@/modules/priceLists/priceListsStore";
import { useUserDisplayNames } from "@/store/teamsStore";
import { OpportunityStatistics } from "@/modules/opportunities/OpportunityStatistics";
import { OpportunityDetailSheet } from "@/modules/opportunities/OpportunityDetailSheet";
import { NotesSection } from "@/components/notes/NotesSection";
import { useNotesStore } from "@/store/notesStore";
import { ShippingAddressesSection } from "./ShippingAddressesSection";
import { UserSelector } from "@/components/UserSelector";
import { PriceListSelector } from "@/components/PriceListSelector";
import PersonDetailSheet from "@/modules/people/PersonDetailSheet";
import { AddressEditSheet } from "./AddressEditSheet";
import { useCanEditOwned, useHasPermission } from "@/store/authStore";
import { useCompanySettingsStore } from "@/store/companySettingsStore";
import { CustomFieldsSection } from "@/components/customFields/CustomFieldsSection";

/**
 * A settings-row style form field with label on the left and control on the right.
 * @param props - Label text and the control rendered on the right
 * @returns Horizontal form row element
 */
function FormRow({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="py-2.5">
      <div className="flex items-center justify-between gap-4">
        <Label
          htmlFor={htmlFor}
          className="text-sm font-medium leading-tight shrink-0 text-muted-foreground"
        >
          {label}
        </Label>
        <div className="w-[320px] shrink-0">{children}</div>
      </div>
    </div>
  );
}

/**
 * Props for the CompanyDetailSheet component.
 */
interface CompanyDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
}

/**
 * Local mirror of the company's editable attributes. Edits are buffered here
 * until the user saves.
 */
interface CompanyFormData {
  name: string;
  email: string;
  phone: string;
  address: Address;
  salesRepUserId: string | null;
  defaultPriceListId: string | null;
  customFields: CustomFieldValues;
}

/**
 * Creates an empty address object with all fields set to null.
 * @returns A new Address with all fields null
 */
const createEmptyAddress = (): Address => ({
  name: null,
  address_line_1: null,
  address_line_2: null,
  city: null,
  state: null,
  zipcode: null,
  country: null,
});

/**
 * Builds form data from a company and its current price list ID.
 * @param company - The company to extract data from
 * @param priceListId - The current default price list ID
 * @returns Form data matching the company's current values
 */
const getFormDataFromCompany = (
  company: Company,
  priceListId: string | null,
): CompanyFormData => ({
  name: company.name,
  email: company.email ?? "",
  phone: company.phone ?? "",
  address: company.address ? { ...company.address } : createEmptyAddress(),
  salesRepUserId: company.salesRepUserId ?? null,
  defaultPriceListId: priceListId,
  customFields: company.customFields ?? {},
});

/**
 * Formats a company address into a single display string.
 * @param address - The address to format
 * @returns Formatted address string or null if no address data
 */
const formatAddressDisplay = (address: Address | null): string | null => {
  if (!address) {
    return null;
  }
  const parts = [
    address.address_line_1,
    address.city,
    address.state,
    address.zipcode,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : null;
};

/**
 * Formats a currency amount string for display.
 * @param amount - The amount string to format
 * @returns Formatted currency string
 */
const formatCurrency = (amount: string): string => {
  const num = parseFloat(amount);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(num);
};

/**
 * A sheet component that displays company details as an always-editable form.
 * Field edits are buffered locally and persisted together when the user clicks
 * Save Changes in the sheet footer.
 * Organizes associated data (deals, people, shipping addresses, notes) in sections
 * and supports stacked sheets for viewing/editing associated people and opportunities.
 * @param props - Sheet control props and the company ID to display
 * @returns The CompanyDetailSheet component
 */
export default function CompanyDetailSheet({
  open,
  onOpenChange,
  companyId,
}: CompanyDetailSheetProps) {
  const { companies, companyLoadingStates, fetchCompany, updateCompany } =
    useCompaniesStore();
  const { people, fetchPeople } = usePeopleStore();
  const shippingAddresses = useCompanyShippingAddressesStore(
    (s) => s.shippingAddresses,
  );
  const fetchShippingAddressesByCompany = useCompanyShippingAddressesStore(
    (s) => s.fetchByCompany,
  );
  const userDisplayNames = useUserDisplayNames();
  const fetchCurrentPriceListForCompany = usePriceListsStore(
    (s) => s.fetchCurrentPriceListForCompany,
  );
  const setCurrentPriceListForCompany = usePriceListsStore(
    (s) => s.setCurrentPriceListForCompany,
  );
  const priceLists = usePriceListsStore((s) => s.priceLists);
  const { updateOpportunity } = useOpportunitiesStore();
  const fetchNotes = useNotesStore((s) => s.fetchNotes);
  const customFieldsSchema = useCompanySettingsStore(
    (s) => s.settings?.customFieldsSchema ?? null,
  );
  const hasFetchedCompanySettings = useCompanySettingsStore(
    (s) => s.hasFetched,
  );
  const fetchCompanySettings = useCompanySettingsStore((s) => s.fetchSettings);

  const company = companies.get(companyId);
  const loadingState = companyLoadingStates.get(companyId);
  const isCompanyLoading = loadingState?.isLoadingGet ?? false;
  const companyError = loadingState?.getError ?? null;

  const [formData, setFormData] = useState<CompanyFormData | null>(null);
  const [initialPriceListId, setInitialPriceListId] = useState<string | null>(
    null,
  );
  const [isAddressDialogOpen, setIsAddressDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingCustomFields, setIsSavingCustomFields] = useState(false);

  const [statistics, setStatistics] =
    useState<OpportunityStatisticsType | null>(null);
  const [statisticsLoading, setStatisticsLoading] = useState(false);
  const [deals, setDeals] = useState<HydratedOpportunity[]>([]);
  const [dealsLoading, setDealsLoading] = useState(false);

  const [editingOpportunityId, setEditingOpportunityId] = useState<
    string | null
  >(null);
  const [viewingPerson, setViewingPerson] = useState<Person | null>(null);

  const isSynced = Boolean(company && company.externalMappings.length > 0);
  const canEdit = useCanEditOwned(
    Permission.COMPANIES_EDIT_ALL,
    Permission.COMPANIES_EDIT_OWN,
    company?.salesRepUserId,
  );
  const canAssignSalesRep = useHasPermission(Permission.COMPANIES_EDIT_ALL);
  // Fields render read-only when the company is ERP-synced or the user lacks edit scope.
  const isReadOnly = isSynced || !canEdit;
  // Custom fields stay editable on synced companies: the ERP does not own them.
  const canEditCustomFields = canEdit;
  const hasCustomFields = Boolean(
    customFieldsSchema && Object.keys(customFieldsSchema.properties).length > 0,
  );
  const savedCustomFields = company?.customFields ?? {};
  const customFieldsDirty = Boolean(
    formData && !isEqual(formData.customFields, savedCustomFields),
  );

  const companyPeople = useMemo(() => {
    const result: Person[] = [];
    people.forEach((person) => {
      if (person.companyId === companyId) {
        result.push(person);
      }
    });
    return result;
  }, [people, companyId]);

  const companyShippingAddressCount = useMemo(() => {
    let count = 0;
    shippingAddresses.forEach((addr) => {
      if (addr.companyId === companyId) {
        count++;
      }
    });
    return count;
  }, [shippingAddresses, companyId]);

  // Fetch data when sheet opens
  useEffect(() => {
    if (!open) {
      return;
    }
    fetchCompany(companyId);
    fetchPeople({ companyId });
    fetchShippingAddressesByCompany(companyId);
    fetchNotes("COMPANY", companyId);
    fetchCompanyStatistics(companyId);
    fetchCompanyDeals(companyId);
  }, [
    open,
    companyId,
    fetchCompany,
    fetchPeople,
    fetchShippingAddressesByCompany,
    fetchNotes,
  ]);

  useEffect(() => {
    if (!open || hasFetchedCompanySettings) {
      return;
    }
    fetchCompanySettings();
  }, [open, hasFetchedCompanySettings, fetchCompanySettings]);

  // Initialize form data when company loads
  useEffect(() => {
    if (!open || !company) {
      return;
    }
    setFormData(getFormDataFromCompany(company, initialPriceListId));
  }, [open, company?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch price list for this company
  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    const fetchPriceList = async () => {
      const resolvedId = await fetchCurrentPriceListForCompany(companyId);
      if (cancelled) {
        return;
      }
      setInitialPriceListId(resolvedId);
      setFormData((prev) =>
        prev ? { ...prev, defaultPriceListId: resolvedId } : prev,
      );
    };
    fetchPriceList();
    return () => {
      cancelled = true;
    };
  }, [open, companyId, fetchCurrentPriceListForCompany]);

  /**
   * Fetches opportunity statistics scoped to the given company.
   * @param id - The company ID to filter statistics by
   */
  const fetchCompanyStatistics = async (id: string) => {
    setStatisticsLoading(true);
    try {
      const response = await fetchApi(
        `opportunities/statistics?companyId=${id}`,
        "GET",
      );
      if (response.ok && response.data) {
        const data = GetOpportunityStatisticsResponseSchema.parse(
          response.data,
        );
        setStatistics(data.statistics);
      }
    } catch (error) {
      captureException(error as Error);
    } finally {
      setStatisticsLoading(false);
    }
  };

  /**
   * Fetches opportunities associated with the given company.
   * @param id - The company ID to filter deals by
   */
  const fetchCompanyDeals = async (id: string) => {
    setDealsLoading(true);
    try {
      const response = await fetchApi(`opportunities?companyId=${id}`, "GET");
      if (response.ok && response.data) {
        const data = ListOpportunitiesResponseSchema.parse(response.data);
        setDeals(data.opportunities);
      }
    } catch (error) {
      captureException(error as Error);
    } finally {
      setDealsLoading(false);
    }
  };

  /**
   * Handles sheet open/close, resetting transient state when closed.
   * @param newOpen - Whether the sheet should be open
   */
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setFormData(null);
      setEditingOpportunityId(null);
      setViewingPerson(null);
    }
    onOpenChange(newOpen);
  };

  /**
   * Buffers an address change from the address dialog (persisted on Save).
   * @param address - The updated address
   */
  const handleAddressSave = (address: Address) => {
    setFormData((prev) => (prev ? { ...prev, address } : prev));
  };

  /**
   * Buffers a sales rep change (persisted on Save).
   * @param salesRepUserId - The selected sales rep user ID, or null to clear
   */
  const handleSalesRepSelect = (salesRepUserId: string | null) => {
    setFormData((prev) => (prev ? { ...prev, salesRepUserId } : prev));
  };

  /**
   * Buffers a default price list change (persisted on Save).
   * @param defaultPriceListId - The selected price list ID, or null to clear
   */
  const handlePriceListSelect = (defaultPriceListId: string | null) => {
    setFormData((prev) => (prev ? { ...prev, defaultPriceListId } : prev));
  };

  /**
   * Persists all pending company edits, including the default price list change,
   * which is saved via its dedicated endpoint.
   */
  const handleSave = async () => {
    if (!company || !formData) {
      return;
    }
    const trimmedName = formData.name.trim();
    if (!trimmedName) {
      toast.error("Company name is required");
      return;
    }

    setIsSaving(true);
    try {
      const updateData: UpdateCompanyData = {
        salesRepUserId: formData.salesRepUserId,
        customFields: formData.customFields,
      };
      if (!isSynced) {
        updateData.name = trimmedName;
        updateData.email = formData.email.trim();
        updateData.phone = formData.phone.trim();
        updateData.address = formData.address;
      }
      const { company: result, error } = await updateCompany(
        companyId,
        updateData,
      );
      if (!result) {
        toast.error(error ?? "Failed to update company");
        return;
      }

      if (formData.defaultPriceListId !== initialPriceListId) {
        const priceListResult = await setCurrentPriceListForCompany({
          companyId,
          priceListId: formData.defaultPriceListId,
        });
        if (formData.defaultPriceListId !== null && priceListResult === null) {
          toast.error("Failed to update price list");
          return;
        }
        setInitialPriceListId(formData.defaultPriceListId);
      }

      setFormData((prev) =>
        prev
          ? {
              ...prev,
              name: result.name,
              email: result.email ?? "",
              phone: result.phone ?? "",
            }
          : prev,
      );
      toast.success("Company updated successfully");
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Persists only the custom field values, which ERP-synced companies accept.
   */
  const handleSaveCustomFields = async () => {
    if (!formData) {
      return;
    }
    setIsSavingCustomFields(true);
    try {
      const { company: result, error } = await updateCompany(companyId, {
        customFields: formData.customFields,
      });
      if (!result) {
        toast.error(error ?? "Failed to update custom fields");
        return;
      }
      toast.success("Custom fields updated");
    } finally {
      setIsSavingCustomFields(false);
    }
  };

  const handleResetCustomFields = () => {
    setFormData((prev) =>
      prev ? { ...prev, customFields: savedCustomFields } : prev,
    );
  };

  /**
   * Handles saving an edited opportunity and refreshes deals/stats.
   * @param data - The opportunity update payload
   * @returns True on success, or an error message string
   */
  const handleOpportunitySave = async (
    data: UpdateOpportunityParams,
  ): Promise<true | string> => {
    if (!editingOpportunityId) {
      return "No opportunity selected";
    }
    const { error } = await updateOpportunity(editingOpportunityId, data);
    if (error) {
      return error;
    }
    fetchCompanyDeals(companyId);
    fetchCompanyStatistics(companyId);
    return true;
  };

  const peopleColumns: DataTableColumn<Person>[] = [
    {
      key: "name",
      header: "Name",
      type: "string",
      format: (_value: unknown, person: Person) =>
        [person.firstName, person.lastName].filter(Boolean).join(" ") ||
        "Unknown",
    },
    {
      key: "email",
      header: "Email",
      type: "string",
      format: (_value: unknown, person: Person) => person.email || "-",
    },
    {
      key: "isPrimaryCompanyContact",
      header: "Role",
      type: "string",
      width: 120,
      format: (_value: unknown, person: Person) =>
        person.isPrimaryCompanyContact ? (
          <Badge variant="secondary">Primary</Badge>
        ) : null,
    },
  ];

  const dealsColumns: DataTableColumn<HydratedOpportunity>[] = [
    { key: "name", header: "Name", type: "string" },
    {
      key: "amount",
      header: "Amount",
      type: "string",
      width: 140,
      format: (_value: unknown, deal: HydratedOpportunity) =>
        formatCurrency(deal.amount),
    },
    {
      key: "status",
      header: "Status",
      type: "string",
      width: 160,
      format: (_value: unknown, deal: HydratedOpportunity) => (
        <Badge variant="secondary">
          {OPPORTUNITY_STATUS_DISPLAY_NAMES[deal.status]}
        </Badge>
      ),
    },
    { key: "createdAt", header: "Created", type: "date", width: 120 },
  ];

  const salesRepName = formData?.salesRepUserId
    ? userDisplayNames.get(formData.salesRepUserId)
    : null;
  const priceListName = formData?.defaultPriceListId
    ? priceLists.get(formData.defaultPriceListId)?.name
    : null;
  const addressDisplay = formatAddressDisplay(formData?.address ?? null);

  return (
    <>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent className="w-1/2 max-w-none flex flex-col p-0">
          <div className="flex-1 overflow-y-auto px-6">
            {companyError && !company ? (
              <div className="flex h-full w-full items-center justify-center p-6">
                <div className="text-center text-destructive">
                  <p>Failed to load company: {companyError}</p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => fetchCompany(companyId)}
                  >
                    Retry
                  </Button>
                </div>
              </div>
            ) : isCompanyLoading && !company ? (
              <div className="flex h-full w-full items-center justify-center p-6">
                <Loader className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : company && formData ? (
              <div className="py-6 flex flex-col gap-6">
                {/* Header: monogram + name */}
                <div className="flex items-center gap-3.5">
                  <div
                    aria-hidden="true"
                    className="flex-none w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"
                  >
                    <span className="text-sm font-semibold text-primary/70">
                      {formData.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0 flex items-center gap-2.5">
                    <h1 className="text-2xl font-bold truncate">
                      {formData.name}
                    </h1>
                    {isSynced && (
                      <Badge
                        variant="outline"
                        className="text-[10px] font-normal tracking-wider uppercase px-1.5 py-0 border-muted-foreground/25"
                      >
                        Synced
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Details form — edits buffered locally, saved via the Save button */}
                <div className="divide-y">
                  <FormRow label="Name" htmlFor="company-name">
                    {isReadOnly ? (
                      <p className="text-sm">{formData.name}</p>
                    ) : (
                      <Input
                        id="company-name"
                        value={formData.name}
                        placeholder="Company name"
                        onChange={(e) =>
                          setFormData((prev) =>
                            prev ? { ...prev, name: e.target.value } : prev,
                          )
                        }
                      />
                    )}
                  </FormRow>

                  <FormRow label="Email" htmlFor="company-email">
                    {isReadOnly ? (
                      <p className="text-sm">{formData.email || "-"}</p>
                    ) : (
                      <Input
                        id="company-email"
                        type="email"
                        value={formData.email}
                        placeholder="Email address"
                        onChange={(e) =>
                          setFormData((prev) =>
                            prev ? { ...prev, email: e.target.value } : prev,
                          )
                        }
                      />
                    )}
                  </FormRow>

                  <FormRow label="Phone" htmlFor="company-phone">
                    {isReadOnly ? (
                      <p className="text-sm">{formData.phone || "-"}</p>
                    ) : (
                      <Input
                        id="company-phone"
                        type="tel"
                        value={formData.phone}
                        placeholder="Phone number"
                        onChange={(e) =>
                          setFormData((prev) =>
                            prev ? { ...prev, phone: e.target.value } : prev,
                          )
                        }
                      />
                    )}
                  </FormRow>

                  <FormRow label="Address">
                    {isReadOnly ? (
                      <p className="text-sm">{addressDisplay ?? "-"}</p>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full justify-between font-normal"
                        onClick={() => setIsAddressDialogOpen(true)}
                      >
                        <span
                          className={
                            addressDisplay
                              ? "truncate"
                              : "text-muted-foreground"
                          }
                        >
                          {addressDisplay ?? "Add address"}
                        </span>
                        <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    )}
                  </FormRow>

                  <FormRow label="Sales Rep">
                    {!canAssignSalesRep ? (
                      <p className="text-sm">{salesRepName ?? "-"}</p>
                    ) : (
                      <UserSelector
                        currentlySelectedId={formData.salesRepUserId}
                        onSelectId={handleSalesRepSelect}
                        placeholder="Select a sales rep"
                      />
                    )}
                  </FormRow>

                  <FormRow label="Price List">
                    {isReadOnly ? (
                      <p className="text-sm">{priceListName ?? "-"}</p>
                    ) : (
                      <PriceListSelector
                        currentlySelectedId={formData.defaultPriceListId}
                        onSelectId={handlePriceListSelect}
                        placeholder="Select a price list"
                      />
                    )}
                  </FormRow>
                </div>

                {/* Stats band */}
                <div className="border-b py-4">
                  <OpportunityStatistics
                    statistics={statistics}
                    isLoading={statisticsLoading}
                    hideWinRate
                  />
                </div>

                {/* Associated data tabs */}
                <Tabs defaultValue="deals" className="pt-1">
                  <TabsList className="flex h-auto min-h-8 w-full flex-wrap">
                    <TabsTrigger value="deals" className="grow px-2 text-xs">
                      Deals
                      <span className="ml-1 tabular-nums">
                        ({deals.length})
                      </span>
                    </TabsTrigger>
                    <TabsTrigger value="people" className="grow px-2 text-xs">
                      People
                      <span className="ml-1 tabular-nums">
                        ({companyPeople.length})
                      </span>
                    </TabsTrigger>
                    <TabsTrigger value="shipping" className="grow px-2 text-xs">
                      Shipping Addresses
                      <span className="ml-1 tabular-nums">
                        ({companyShippingAddressCount})
                      </span>
                    </TabsTrigger>
                    <TabsTrigger value="notes" className="grow px-2 text-xs">
                      Notes
                    </TabsTrigger>
                    {hasCustomFields && (
                      <TabsTrigger
                        value="custom-fields"
                        className="grow px-2 text-xs"
                      >
                        Custom Fields
                      </TabsTrigger>
                    )}
                  </TabsList>

                  <TabsContent value="deals" className="mt-4 space-y-5">
                    <DataTable
                      columns={dealsColumns}
                      data={deals}
                      isLoading={dealsLoading && deals.length === 0}
                      hideSearch
                      onClickRow={(deal) => setEditingOpportunityId(deal.id)}
                    />
                  </TabsContent>

                  <TabsContent value="people" className="mt-4 space-y-5">
                    <DataTable
                      columns={peopleColumns}
                      data={companyPeople}
                      hideSearch
                      onClickRow={(person) => setViewingPerson(person)}
                    />
                  </TabsContent>

                  <TabsContent value="shipping" className="mt-4 space-y-5">
                    <ShippingAddressesSection companyId={companyId} />
                  </TabsContent>

                  <TabsContent value="notes" className="mt-4 space-y-5">
                    <NotesSection entityType="COMPANY" entityId={companyId} />
                  </TabsContent>

                  {hasCustomFields && customFieldsSchema && (
                    <TabsContent
                      value="custom-fields"
                      className="mt-4 space-y-3"
                    >
                      {customFieldsDirty && canEditCustomFields && (
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleResetCustomFields}
                            disabled={isSavingCustomFields}
                            className="h-8"
                          >
                            <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                            Reset
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleSaveCustomFields}
                            disabled={isSavingCustomFields}
                            className="h-8"
                          >
                            {isSavingCustomFields ? (
                              <Loader className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Check className="mr-1.5 h-3.5 w-3.5" />
                            )}
                            Save
                          </Button>
                        </div>
                      )}
                      <CustomFieldsSection
                        schema={customFieldsSchema}
                        values={formData.customFields}
                        readOnly={!canEditCustomFields}
                        layout="rows"
                        onChange={(fieldName, value) =>
                          setFormData((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  customFields: {
                                    ...prev.customFields,
                                    [fieldName]: value,
                                  },
                                }
                              : prev,
                          )
                        }
                      />
                    </TabsContent>
                  )}
                </Tabs>
              </div>
            ) : null}
          </div>
          {company &&
            formData &&
            canEdit &&
            (!isSynced || canAssignSalesRep) && (
              <div className="flex justify-end gap-2 border-t p-4">
                <Button
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={isSaving}>
                  {isSaving && <Loader className="h-4 w-4 mr-2 animate-spin" />}
                  Save Changes
                </Button>
              </div>
            )}
        </SheetContent>
      </Sheet>

      {/* Address edit sheet */}
      {company && formData && !isSynced && (
        <AddressEditSheet
          open={isAddressDialogOpen}
          onOpenChange={setIsAddressDialogOpen}
          address={formData.address}
          onSave={handleAddressSave}
        />
      )}

      {/* Stacked opportunity detail sheet */}
      {editingOpportunityId && (
        <OpportunityDetailSheet
          open={!!editingOpportunityId}
          onOpenChange={(newOpen: boolean) =>
            !newOpen && setEditingOpportunityId(null)
          }
          opportunityId={editingOpportunityId}
          onSave={handleOpportunitySave}
          mode="edit"
        />
      )}

      {/* Stacked person detail sheet */}
      {viewingPerson && (
        <PersonDetailSheet
          open={!!viewingPerson}
          onOpenChange={(newOpen) => {
            if (!newOpen) {
              setViewingPerson(null);
            }
          }}
          personId={viewingPerson.id}
        />
      )}
    </>
  );
}
